/**
 * Shared modal/drawer behaviour: Escape to close, a focus trap while open, and focus
 * restored to whatever opened it.
 *
 * The prototype had none of this — its overlays were plain divs — so this is purely
 * additive: it changes no pixel, only what the keyboard can reach. It lives beside the
 * primitives because Modal and Drawer must behave identically; a dialog that traps
 * focus differently from its sibling is a bug waiting to happen.
 */
import { useCallback, useEffect, useRef, type MouseEvent, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Hidden-ness is tested with attributes and computed style rather than
 * `offsetParent`, which is always null under jsdom and would collapse the trap to
 * nothing in tests.
 */
function isVisible(element: HTMLElement): boolean {
  if (element.hasAttribute("hidden") || element.closest("[hidden]")) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  return !style || (style.display !== "none" && style.visibility !== "hidden");
}

/**
 * Tab-reachable descendants, in DOM order.
 *
 * The `tabIndex >= 0` filter is load-bearing: a roving tab strip renders every
 * unselected tab as `tabindex="-1"`, and those still match `button:not([disabled])`.
 * Counting them would make the trap think the last tab stop is further along than it
 * is, so Tab off the real last stop would leave the dialog instead of wrapping.
 */
function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => isVisible(element) && element.tabIndex >= 0,
  );
}

/* ------------------------------ Escape stack ------------------------------ */

/**
 * Every open overlay, in the order it opened. Escape belongs to the last one — the one
 * drawn on top — and to nothing else.
 *
 * The reason this is a module-level stack rather than a listener per dialog: every
 * overlay listens on `document`, and DOM listeners on the same node fire in
 * *registration* order, not stacking order. So a modal opened on top of a drawer had
 * its Escape handled by the drawer underneath, which closed and then stopped
 * propagation — one keypress dismissed the wrong overlay and left the obscuring one up.
 * No amount of `stopPropagation` fixes that, because the wrong handler runs first.
 *
 * With one shared listener the question "who is on top?" is answered by the stack
 * instead of by registration order, and `stopImmediatePropagation` keeps any *other*
 * document-level Escape listener — a notification popover, say — from also reacting.
 */
interface OverlayEntry {
  close: () => void;
}

const overlays: OverlayEntry[] = [];
let escapeListenerAttached = false;

function handleDocumentEscape(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  const topmost = overlays[overlays.length - 1];
  if (!topmost) return;
  event.stopImmediatePropagation();
  topmost.close();
}

function pushOverlay(entry: OverlayEntry): () => void {
  overlays.push(entry);
  if (!escapeListenerAttached) {
    document.addEventListener("keydown", handleDocumentEscape, true);
    escapeListenerAttached = true;
  }
  return () => {
    const index = overlays.lastIndexOf(entry);
    if (index !== -1) overlays.splice(index, 1);
    if (overlays.length === 0 && escapeListenerAttached) {
      document.removeEventListener("keydown", handleDocumentEscape, true);
      escapeListenerAttached = false;
    }
  };
}

/**
 * Closes this overlay on Escape, but only while it is the topmost one.
 *
 * `useDialog` calls this for you — a dialog is mounted only while it is open, so it
 * leaves `active` alone. A control that stays mounted and merely *shows* an overlay
 * (the sidebar's user menu, a dropdown) must pass its open state, or it would sit in
 * the stack permanently and swallow every Escape in the app.
 */
export function useOverlayEscape(onClose: () => void, active = true): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!active) return;
    return pushOverlay({ close: () => closeRef.current() });
  }, [active]);
}

/* --------------------------------- useDialog ------------------------------ */

export interface UseDialogOptions {
  onClose: () => void;
  /** Set false for a dialog that should not steal focus on mount. */
  autoFocus?: boolean;
}

export interface UseDialogResult<T extends HTMLElement> {
  /** Attach to the dialog panel (not the overlay). */
  panelRef: RefObject<T>;
  /** Attach to the overlay's onMouseDown — closes only on a genuine backdrop click. */
  onOverlayMouseDown: (event: MouseEvent<HTMLElement>) => void;
}

export function useDialog<T extends HTMLElement>({
  onClose,
  autoFocus = true,
}: UseDialogOptions): UseDialogResult<T> {
  const panelRef = useRef<T>(null);
  // Kept in a ref so re-rendering the dialog with a new handler doesn't re-run the
  // effect and steal focus back from whatever the user is typing in.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Escape is the stack's business, not this dialog's — see the note above.
  useOverlayEscape(onClose);

  useEffect(() => {
    const panel = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    if (autoFocus && panel) {
      const [first] = focusableWithin(panel);
      (first ?? panel).focus({ preventScroll: true });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = focusableWithin(panelRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!panelRef.current.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      // The opener may have unmounted (a row that got filtered away) — guard it.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [autoFocus]);

  const onOverlayMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    // Only a press that starts *and* lands on the backdrop counts, so selecting text
    // inside the dialog and releasing outside it never closes the dialog.
    if (event.target === event.currentTarget) closeRef.current();
  }, []);

  return { panelRef, onOverlayMouseDown };
}
